'use client';

import { Component, ReactNode } from 'react';
import { RuntimeErrorCard } from '@/components/errors/RuntimeErrorCard';
import {
  clearRuntimeCaches,
  computeClientErrorFingerprint,
  consumeAutomaticRecoveryAttempt,
  createRecoveryId,
  isChunkOrModuleLoadError,
  reportClientError,
} from '@/lib/client-errors';

interface Props {
  children: ReactNode;
  context?: 'app' | 'workspace';
  onGoToExplore?: () => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
  errorCount: number;
  recoveryId?: string;
  fingerprint?: string;
}

class ErrorBoundary extends Component<Props, State> {
  private errorTimer?: NodeJS.Timeout;
  private mounted = false;
  /** ChunkLoadError 自动恢复的最大重试次数 */
  private static CHUNK_RELOAD_KEY = 'eb-chunk-reload';
  private static CHUNK_RELOAD_MAX = 2;

  constructor(props: Props) {
    super(props);
    this.state = { 
      hasError: false,
      errorCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidMount() {
    this.mounted = true;
    // Storage can be disabled independently of rendering (for example by the
    // browser's privacy settings). Never let optional recovery bookkeeping
    // prevent an otherwise healthy workspace from mounting.
    try {
      window.sessionStorage.removeItem(ErrorBoundary.CHUNK_RELOAD_KEY);
    } catch {
      // Automatic recovery stays disabled when its durable budget is unavailable.
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);

    const recoveryId = createRecoveryId();
    const fingerprint = computeClientErrorFingerprint(error, errorInfo.componentStack);
    
    this.setState((prev) => ({
      errorInfo,
      errorCount: prev.errorCount + 1,
      recoveryId,
      fingerprint,
    }));
    void reportClientError({
      recoveryId,
      error,
      componentStack: errorInfo.componentStack,
      context: this.props.context ?? 'app',
      extra: {
        errorCount: this.state.errorCount + 1,
      },
    }).then((result) => {
      if (!this.mounted) return;
      this.setState({
        recoveryId: result.recoveryId,
        fingerprint: result.fingerprint,
      });
    });

    // 如果短时间内发生多次错误，可能是无限循环
    if (this.errorTimer) {
      clearTimeout(this.errorTimer);
    }
    
    this.errorTimer = setTimeout(() => {
      this.setState({ errorCount: 0 });
    }, 5000);

    if (typeof window !== 'undefined') {
      // 存储错误信息以便调试
      const errorLog = {
        recoveryId,
        fingerprint,
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
      };
      
      try {
        const existingLogs = JSON.parse(localStorage.getItem('error-logs') || '[]');
        existingLogs.push(errorLog);
        // 只保留最近 10 条错误
        localStorage.setItem('error-logs', JSON.stringify(existingLogs.slice(-10)));
      } catch (e) {
        console.error('[ErrorBoundary] Failed to save error log:', e);
      }
    }

    // ── Chunk / dynamic import 自动恢复 ──
    // 部署新版本后旧 JS chunk 不存在，自动刷新加载新版本。
    if (isChunkOrModuleLoadError(error)) {
      try {
        const storage = window.sessionStorage;
        const reloadCount = Number(storage.getItem(ErrorBoundary.CHUNK_RELOAD_KEY) || '0');
        if (
          reloadCount < ErrorBoundary.CHUNK_RELOAD_MAX
          && consumeAutomaticRecoveryAttempt('chunk', fingerprint)
        ) {
          storage.setItem(ErrorBoundary.CHUNK_RELOAD_KEY, String(reloadCount + 1));
          void this.reloadAfterCacheClear();
          return;
        }
        storage.removeItem(ErrorBoundary.CHUNK_RELOAD_KEY);
      } catch {
        // Without a persisted retry budget, keep the manual recovery card.
        // A full reload here could repeat forever after the next mount.
        return;
      }
    }

    // Workspace render errors are usually safest to recover with one full route reload.
    if (
      (this.props.context ?? 'app') === 'workspace'
      && consumeAutomaticRecoveryAttempt('workspace-render', fingerprint)
    ) {
      window.setTimeout(() => window.location.reload(), 120);
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    if (this.errorTimer) {
      clearTimeout(this.errorTimer);
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleClearAndReload = () => {
    // 只清浏览器缓存，保留 octo-auth / logged_in 等登录恢复信号。
    try {
      sessionStorage.removeItem(ErrorBoundary.CHUNK_RELOAD_KEY);
    } catch (e) {
      console.error('Failed to clear chunk reload marker:', e);
    }
    void this.reloadAfterCacheClear();
  };

  reloadAfterCacheClear = async () => {
    try {
      await clearRuntimeCaches();
    } catch (e) {
      console.error('Failed to clear runtime caches:', e);
    }
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ 
      hasError: false, 
      error: undefined,
      errorInfo: undefined,
      errorCount: 0,
    });
  };

  render() {
    if (this.state.hasError) {
      const isRepeatedError = this.state.errorCount > 2;

      return (
        <RuntimeErrorCard
          context={this.props.context ?? 'app'}
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          errorCount={this.state.errorCount}
          recoveryId={this.state.recoveryId}
          isRepeatedError={isRepeatedError}
          onRetry={(this.props.context ?? 'app') === 'workspace' || isRepeatedError ? undefined : this.handleReset}
          onRefresh={this.handleReload}
          onClearCache={this.handleClearAndReload}
          onGoToExplore={this.props.onGoToExplore}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
